import { useState } from 'react';
import { errorMessage } from '../api/client';
import { formatFileSize, versionContentUrl, type DocumentVersion } from '../api/versions';
import { formatDateTime } from '../lib/date-only';
import { Button } from './button';

export interface VersionHistoryListProps {
  roomId: string;
  documentId: string;
  /** Newest first, as the API serves them; undefined while loading. */
  versions: readonly DocumentVersion[] | undefined;
  /** The history could not be read. */
  error?: unknown;
  /** "Split back out" (the room admin only); omitted = never offered. */
  onSplit?: (versionId: string) => void;
  /** The version whose split is in flight, or null. */
  splittingId?: string | null;
  /** The last split's error, or null. */
  splitError?: unknown;
}

/**
 * A document's VERSION HISTORY (ADR-0031 amendment 2026-10-13): every version
 * — its number, who uploaded it and when, its file name and size, and which one
 * is current — with "Open" and "Download" for each (its REDACTED copy, the same
 * redactions everyone in the data room sees — owner, 2026-10-16; "Being
 * prepared" while a version has none yet), and, for the room admin, a
 * version that came in with a merge can be split back out to a document of its
 * own (asked twice). Presentational: the app owns the query and the split. Shown
 * to the room admin and the bound seller only; the API refuses everyone else.
 */
export function VersionHistoryList({
  roomId,
  documentId,
  versions,
  error = null,
  onSplit,
  splittingId = null,
  splitError = null,
}: VersionHistoryListProps) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const readError = errorMessage(error);
  const actionError = errorMessage(splitError);
  return (
    <section aria-label="Version history" className="space-y-2">
      <p className="font-medium">Version history</p>
      {readError ? (
        <p className="text-sm text-destructive" role="alert">
          {readError}
        </p>
      ) : !versions ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <ol className="space-y-1.5">
          {versions.map((v) => (
            <li key={v.id} className="rounded border px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="shrink-0 font-semibold">Version {v.versionNo}</span>
                  {v.isCurrent ? (
                    <span className="shrink-0 rounded-full bg-st-certified px-2 py-0.5 text-xs font-medium text-st-certified-fg">
                      Current
                    </span>
                  ) : null}
                </span>
                {v.beingPrepared ? (
                  <span
                    className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                    title="The redacted copy of this version is being prepared."
                  >
                    Being prepared
                  </span>
                ) : (
                  <span className="flex shrink-0 flex-wrap gap-3 text-[13px] font-semibold">
                    <a
                      className="text-primary hover:underline"
                      href={versionContentUrl(roomId, documentId, v.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Open version ${v.versionNo}`}
                    >
                      Open
                    </a>
                    <a
                      className="text-primary hover:underline"
                      href={versionContentUrl(roomId, documentId, v.id, true)}
                      aria-label={`Download version ${v.versionNo}`}
                    >
                      Download
                    </a>
                  </span>
                )}
              </div>
              <p className="mt-0.5 break-words">{v.fileName ?? 'Untitled file'}</p>
              <p className="text-xs text-muted-foreground">
                {[
                  v.uploadedBy ? `Uploaded by ${v.uploadedBy}` : null,
                  formatDateTime(v.uploadedAt),
                  formatFileSize(v.sizeBytes),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {onSplit && v.canSplit ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {confirming === v.id ? (
                    <>
                      <span className="text-xs text-muted-foreground">
                        It becomes a document of its own again and is never merged back
                        automatically.
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setConfirming(null);
                          onSplit(v.id);
                        }}
                        disabled={splittingId !== null}
                      >
                        {splittingId === v.id ? 'Splitting…' : 'Yes, split it out'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirming(v.id)}
                      disabled={splittingId !== null}
                      aria-label={`Split version ${v.versionNo} back out`}
                    >
                      {splittingId === v.id ? 'Splitting…' : 'Split back out'}
                    </Button>
                  )}
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      {actionError ? (
        <p className="text-sm text-destructive" role="alert">
          {actionError}
        </p>
      ) : null}
    </section>
  );
}
